
import ConfigurationService from '../services/ConfigurationService';
/**
 * Auth Session Constants
 *
 * Keys used for managing authentication session state.
 * These keys are used in sessionStorage for client-side session tracking.
 *
 * @security These keys store session state ONLY. No tokens or secrets are stored.
 * @security sessionStorage is used (not localStorage) to ensure data is cleared on browser close.
 */
export const AUTH_SESSION_KEYS = {
  /**
   * Tracks if a token refresh has failed in the current browser session.
   * Used to prevent infinite refresh loops when tokens expire.
   * Cleared on successful login or explicit logout.
   */
  REFRESH_FAILED: 'sp:auth:refresh_failed',
  /**
   * Temporary token used for 2FA verification.
   */
  PARTIAL_TOKEN: 'sp:auth:partial_token',
  /**
   * Temporary token used for first-login password change.
   */
  FIRST_LOGIN_TOKEN: 'sp:auth:first_login_token',
  /**
   * SessionStorage key for the session-bound encryption seed.
   */
  ENCRYPTION_SEED: 'sp:crypto:seed',
} as const;

/**
 * Storage sanitizer configuration for guaranteed session isolation.
 * Keys defined here will survive the purgeAll() execution upon logout.
 */
export const STORAGE_SANITIZER_WHITELIST_KEYS = [
  'theme',
  'color-theme',
  'language',
  'sidebar_collapsed',
  'vite-ui-theme', // Vite/Tailwind themes
] as const;

export const STORAGE_SANITIZER_WHITELIST_PREFIXES = [
  'chakra-',
  'headlessui-',
  'sp:theme:mode:',
  'sp:ui:',
] as const;

/**
 * Namespace for cross-tab session synchronization broadcast channel.
 */
export const SESSION_BROADCAST_CHANNEL_NAME = 'mysfvs_auth_sync_channel';




/**
 * Dynamic Redirect Whitelist Getter
 * @security Prevents Open Redirect (CWE-601) vulnerabilities.
 * Combines dynamically retrieved whitelisted external origins with dynamic same-origin paths.
 * Enforces deep freezing to mitigate client-side script runtime tampering.
 */
export const getDynamicRedirectWhitelist = (): readonly string[] => {
  // 1. Fetch strictly governed external origins from Configuration Layer
  const externalOrigins = ConfigurationService.getAllowedRedirectOrigins();

  // 2. Resolve safe contextual same-origin paths dynamically
  const safeSameOrigin = typeof window !== 'undefined' && 
    (window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? [`${window.location.protocol}//${window.location.hostname}`]
      : [];

  // 3. Return unified, frozen list to prevent prototype pollution or XSS tampering
  return Object.freeze([...externalOrigins, ...safeSameOrigin]);
};



/**
 * Sensitive field keys for redaction/masking in UI.
 * @security Aligned with backend redactionConfig.ts patterns.
 */
export const SENSITIVE_KEYS = [
  'password',
  'secret',
  'token',
  'apikey',
  'authkey',
  'privatekey',
  'proxy_password',
  'secretkey',
  'passphrase',
  'authorization',
  'bearer',
  'session',
  'cookie',
  'signature',
  'accesstoken',
  'refreshtoken',
  'dburl',
  'databaseurl',
  'connectionstring',
  'smtppass',
  'smtppassword',
  'mailpass',
  'mailpassword',
  'clientsecret',
  'appsecret',
  'webhooksecret',
  'key', // Generic but necessary for many gateway configs
] as const;

export const MASK_PLACEHOLDER = '********';

/**
 * Password Strength Score Thresholds (percentage-based)
 * Defines the cut-off points for Weak / Medium / Strong UI indicator.
 * @security Aligned with backend password policy validators.
 */
export const PASSWORD_STRENGTH_THRESHOLDS = {
  /** Percentage below which a password is rated "Weak" */
  WEAK_MAX: 40,
  /** Percentage below which a password is rated "Medium" (>= WEAK_MAX && < MEDIUM_MAX) */
  MEDIUM_MAX: 80,
} as const;

/**
 * The confirmation phrase a user must type to confirm a destructive account deletion.
 * Centralised here to keep it out of component-level magic strings.
 * @security Keep this in sync with backend confirmation endpoint requirements.
 */
export const DELETE_ACCOUNT_CONFIRM_PHRASE = 'DELETE' as const;

/**
 * Cryptographic Hardening Constants
 * Used by cryptoStorageService for AES-GCM 256-bit encryption and PBKDF2 derivation.
 */
export const CRYPTO_CONFIG = {
  /** Current schema version for EncryptedBlob format. */
  BLOB_SCHEMA_VERSION: 1,
  /** PBKDF2 iteration count — balance between security and browser performance. */
  PBKDF2_ITERATIONS: 100_000,
  /** Minimum safe iterations threshold to prevent misconfiguration downgrades. */
  MIN_PBKDF2_ITERATIONS: 10_000,
  /** AES-GCM key length in bits. */
  AES_KEY_LENGTH_BITS: 256,
  /** Initialization vector length in bytes (NIST recommendation for AES-GCM). */
  IV_BYTE_LENGTH: 12,
  /** Salt length in bytes for PBKDF2 key derivation. */
  SALT_BYTE_LENGTH: 16,
} as const;

